<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="dashboard-container flex">
    <SigmaDashboard
      v-if="!loading"
      :embed-url="embedUrl"
      @event="onDashboardClickEvent"
    />
  </div>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { onMounted, ref, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import {
  provideDashboardVersionBanner,
  useToast,
} from '@rygen/rygen-ui/composables';
import { Optional } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useDashboardVersion } from '@/composables';
import {
  DashboardSummary,
  DashboardVersion,
  ShipmentHealth,
  ShipmentSearchCriteria,
  ShipmentStatus,
} from '@/model';

import SigmaDashboard from '@/components/dashboard/SigmaDashboard.vue';

const { t } = useI18n();
const router = useRouter();
const toast = useToast();

const dashboard: Ref<Optional<DashboardSummary>> = ref();
const loading: Ref<boolean> = ref(false);

const { isVnextAvailable, dashboardVersion, embedUrl } =
  useDashboardVersion(dashboard);

const loadDashboard = async (): Promise<void> => {
  try {
    loading.value = true;
    dashboard.value = await api.dashboard.getHomePage();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading dashboard authorization');
    toast.addError({
      detail: t('errors.loadingParam', { param: t('dashboards.dashboards') }),
      error: e,
    });
  } finally {
    loading.value = false;
  }
};

const onVersionChange = (useVnext: boolean) => {
  dashboardVersion.value = useVnext
    ? DashboardVersion.VNEXT
    : DashboardVersion.CURRENT;
};

const onDashboardClickEvent = (event: any) => {
  if (event.data?.type !== 'workbook:chart:onvalueselect') {
    return;
  }

  const title = event.data?.title;
  switch (title) {
    case 'At Risk':
      navigateToAtRiskShipments();
      break;
    case 'Critical':
      navigateToCriticalShipments();
      break;
    case 'Tender Rejected':
      navigateToTenderRejectedShipments();
      break;
  }
};

const navigateToAtRiskShipments = () => {
  const searchCriteria = new ShipmentSearchCriteria({
    statuses: [
      ShipmentStatus.BOOKED,
      ShipmentStatus.CARRIER_ASSIGNED,
      ShipmentStatus.IN_TRANSIT,
      ShipmentStatus.PLANNING,
      ShipmentStatus.TENDERED,
      ShipmentStatus.TENDER_REJECTED,
    ],
    healths: [ShipmentHealth.AT_RISK],
  });

  navigateToShipmentList(searchCriteria);
};

const navigateToCriticalShipments = () => {
  const searchCriteria = new ShipmentSearchCriteria({
    statuses: [
      ShipmentStatus.BOOKED,
      ShipmentStatus.CARRIER_ASSIGNED,
      ShipmentStatus.IN_TRANSIT,
      ShipmentStatus.PLANNING,
      ShipmentStatus.TENDERED,
      ShipmentStatus.TENDER_REJECTED,
    ],
    healths: [ShipmentHealth.CRITICAL],
  });

  navigateToShipmentList(searchCriteria);
};

const navigateToTenderRejectedShipments = () => {
  const searchCriteria = new ShipmentSearchCriteria({
    statuses: [ShipmentStatus.TENDER_REJECTED],
  });

  navigateToShipmentList(searchCriteria);
};

const navigateToShipmentList = (criteria: ShipmentSearchCriteria) => {
  router.push({
    name: 'shipmentList',
    query: { search: JSON.stringify(criteria) },
  });
};

// Sigma embed urls are signed with a single use JWT, so switching versions has
// to fetch a freshly signed url rather than reuse the one already rendered
watch(dashboardVersion, () => {
  loadDashboard();
});

provideDashboardVersionBanner({
  visible: isVnextAvailable,
  vNextActive: () => dashboardVersion.value === DashboardVersion.VNEXT,
  deprecationDate: () => dashboard.value?.vnextDeprecationDate,
  mandatorySwitchDate: () => dashboard.value?.vnextMandatorySwitchDate,
  onChange: onVersionChange,
});

onMounted(() => {
  loadDashboard();
});
</script>

<style>
.dashboard-container {
  flex-direction: column;
  gap: var(--sp-2);

  .sigma-dashboard {
    flex: 1 1 auto;
    min-height: 0;
  }
}
</style>
